import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';
/**
 * @page QRCheckInPage
 * @description QR Code 掃碼報到頁面
 * 手動輸入 QR payload 或（未來）攝影機掃碼
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import type { MemberDto } from '@pokerroom/types';

interface QrCheckInResult {
  member: MemberDto;
  hasDebt: boolean;
  isBlacklisted: boolean;
  isInactive: boolean;
}

export default function QRCheckInPage() {
  const { t } = useTranslation('member');
  const { t: tc } = useTranslation('common');
  const [qrInput, setQrInput] = useState('');

  const checkInMutation = useMutation({
    mutationFn: (qrPayload: string) =>
      api.post<QrCheckInResult>('/members/qr-check-in', { qrPayload }),
    onError: (err) => {
      console.warn('[QRCheckIn] check-in failed:', (err as Error).message);
    },
  });

  const result = checkInMutation.data;
  const error = checkInMutation.error as { response?: { data?: { message?: string } } } | null;

  const handleScan = () => {
    const payload = qrInput.trim();
    if (!payload) return;
    checkInMutation.mutate(payload);
  };

  return (
    <AdminPage
      contentClassName="admin-page-body max-w-lg"
      header={
        <AdminPageHeader
          title={t('qrCheckIn', { defaultValue: 'QR Code Check-in' })}
          back={
            <Link
              to="/check-in"
              className="inline-flex min-h-11 items-center text-base-content/50 hover:text-primary text-sm"
            >
              &larr; {t('backToCheckIn', { defaultValue: 'Back' })}
            </Link>
          }
        />
      }
    >
      {/* Manual input */}
      <div className="bg-base-200 border border-base-300 rounded-xl p-5 mb-6">
        <label className="block text-sm text-base-content/70 mb-2">
          {t('qrPayloadInput', { defaultValue: 'Scan or enter QR Code' })}
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={qrInput}
            onChange={(e) => setQrInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleScan()}
            placeholder="ALLINI-MEMBER:..."
            autoFocus
            className="flex-1 px-3 py-2.5 rounded-lg border border-base-300 bg-base-300 text-base-content text-sm font-mono focus:outline-hidden focus:ring-2 focus:ring-primary"
          />
          <button
            onClick={handleScan}
            disabled={!qrInput.trim() || checkInMutation.isPending}
            className="px-4 py-2.5 rounded-lg bg-primary text-primary-content text-sm font-medium disabled:opacity-50"
          >
            {checkInMutation.isPending ? tc('loading') : t('scan', { defaultValue: 'Scan' })}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 px-4 py-3 bg-error/10 border border-error/30 rounded-lg text-sm text-error">
          {error.response?.data?.message ??
            t('qrCheckInFailed', { defaultValue: 'Check-in failed' })}
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="bg-base-200 border border-base-300 rounded-xl p-5">
          {/* Warnings */}
          {result.isBlacklisted && (
            <div className="mb-3 px-3 py-2 bg-error/10 border border-error/30 rounded-lg text-sm text-error font-medium">
              {t('blacklisted', { defaultValue: 'Blacklisted' })}
            </div>
          )}
          {result.isInactive && (
            <div className="mb-3 px-3 py-2 bg-warning/10 border border-warning/30 rounded-lg text-sm text-warning font-medium">
              {t('status_INACTIVE')}
            </div>
          )}
          {result.hasDebt && (
            <div className="mb-3 px-3 py-2 bg-warning/10 border border-warning/30 rounded-lg text-sm text-warning">
              {t('hasDebt', { defaultValue: 'Has outstanding debt' })}
            </div>
          )}

          {/* Member info */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-base-content/60">{t('nickname')}</span>
              <span className="text-sm font-medium">{result.member.nickname}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-base-content/60">{t('memberNo')}</span>
              <span className="text-sm font-mono">{result.member.memberNo}</span>
            </div>
            {result.member.phone && (
              <div className="flex justify-between">
                <span className="text-sm text-base-content/60">{t('phone')}</span>
                <span className="text-sm">{result.member.phone}</span>
              </div>
            )}
          </div>

          <div className="mt-4 flex gap-2">
            <Link
              to={`/members/${result.member.id}`}
              className="flex-1 py-2 rounded-lg border border-base-300 text-sm text-center hover:bg-base-300"
            >
              {t('viewDetail', { defaultValue: 'View Detail' })}
            </Link>
            <button
              onClick={() => {
                setQrInput('');
                checkInMutation.reset();
              }}
              className="flex-1 py-2 rounded-lg bg-primary text-primary-content text-sm font-medium"
            >
              {t('scanNext', { defaultValue: 'Scan Next' })}
            </button>
          </div>
        </div>
      )}
    </AdminPage>
  );
}
